import type { Clip, ClipTransform, TimeMs } from '../timeline/types';
import { computeClipTransformAt, type Viewport } from '../timeline/sync/animation-runtime';
import type { AudioLayer, Layer, LayerId, PieceModel } from './types';

type VisualLayer = Exclude<Layer, AudioLayer>;

/**
 * Transforms once the reflow resolved derived geometry. Until that reflow moves to
 * the model, they are read from the mounted objects.
 */
export type CanonicalTransforms = Record<LayerId, ClipTransform>;

export type ResolvedLayer = {
  id: LayerId;
  visible: boolean;
  transform: ClipTransform;
  /** Seconds into the source. Video layers only. */
  mediaTime?: number;
};

export type FrameState = {
  /** Back to front. */
  layers: ResolvedLayer[];
};

const IDENTITY: ClipTransform = { left: 0, top: 0, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 };

type Entry = {
  id: LayerId;
  clip: Clip;
  trackVisible: boolean;
  isVideo: boolean;
  canonical: ClipTransform;
};

const isAlive = (clip: Clip, t: TimeMs): boolean =>
  !clip.duration || (t >= clip.start && t < clip.start + clip.duration);

const entryOf = (layer: VisualLayer, canonical: CanonicalTransforms): Entry => ({
  id: layer.id,
  clip: {
    id: layer.id,
    kind: 'fabric',
    trackId: `track_${layer.id}`,
    objectId: layer.id,
    start: layer.start,
    duration: layer.duration,
    fit: layer.fit,
    ...(layer.enter ? { enter: layer.enter } : {}),
    ...(layer.exit ? { exit: layer.exit } : {})
  },
  trackVisible: layer.visible,
  isVideo: layer.kind === 'video',
  canonical: canonical[layer.id] ?? IDENTITY
});

/**
 * Resolves the piece at an arbitrary time into plain data: what is on screen,
 * where, and which source frame each video needs. No Fabric, no DOM, no clock.
 * What does not depend on time is worked out once, so each frame stays cheap.
 */
export const createFrameResolver = (model: PieceModel, canonical: CanonicalTransforms) => {
  const viewport: Viewport = { width: model.width, height: model.height };

  const entries: Entry[] = [];
  for (const id of model.layerOrder) {
    const layer = model.layers[id];
    if (!layer || layer.kind === 'audio') continue;
    entries.push(entryOf(layer, canonical));
  }

  return (t: TimeMs): FrameState => ({
    layers: entries.map(entry => {
      const alive = isAlive(entry.clip, t);
      const resolved: ResolvedLayer = {
        id: entry.id,
        visible: entry.trackVisible && alive,
        transform: computeClipTransformAt(entry.clip, t, viewport, entry.canonical)
      };
      if (entry.isVideo && alive) {
        resolved.mediaTime = Math.max(0, (t - entry.clip.start) / 1000);
      }
      return resolved;
    })
  });
};
